import { useCallback } from 'react'

import * as anchor from '@coral-xyz/anchor'
import {
    createAssociatedTokenAccountInstruction,
    createSyncNativeInstruction,
    getAccount,
    getAssociatedTokenAddress,
} from '@solana/spl-token'
import { useWallet } from '@solana/wallet-adapter-react'
import { PublicKey, SystemProgram, Transaction } from '@solana/web3.js'

import { getHighPriorityConnection } from '@src/chains/solana/anchor/setup'
import { confirmTransactionWithRetry, sendTransactionWithRetry } from '@src/chains/solana/utils/retryConnection'
import { ChainId, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { createContextLogger } from '@src/utils'

const logger = createContextLogger('useWrapSOL')

export const useWrapSOL = () => {
    const { publicKey, signTransaction } = useWallet()

    /**
     * Prepares a VersionedTransaction for wrapping SOL (to WSOL) without signing it.
     * This helper builds the transaction with all required instructions and returns the
     * VersionedTransaction along with the blockhash.
     */
    const getWrapData = useCallback(
        async (
            lamports: bigint,
            publicKey: PublicKey
        ): Promise<{ versionedTransaction: anchor.web3.VersionedTransaction; blockhash: string }> => {
            if (!publicKey) throw new Error('Wrong public key')
            if (!lamports || lamports <= BigInt(0)) {
                throw new Error('Must wrap a positive amount of SOL (lamports)')
            }

            const connection = getHighPriorityConnection()
            const wsolMint = new PublicKey(WRAPPED_GAS_TOKENS[ChainId.Solana].address)
            const wsolATA = await getAssociatedTokenAddress(wsolMint, publicKey)
            const instructions = []

            // Check if the associated token account (ATA) exists.
            let ataInfo
            try {
                ataInfo = await getAccount(connection, wsolATA)
            } catch (err) {
                // If getAccount fails, the ATA likely doesn't exist.
            }

            if (!ataInfo) {
                instructions.push(
                    createAssociatedTokenAccountInstruction(
                        publicKey, // payer
                        wsolATA, // ATA to create
                        publicKey, // owner of the ATA
                        wsolMint // mint
                    )
                )
            }

            // Transfer lamports from the wallet to the ATA.
            instructions.push(
                SystemProgram.transfer({
                    fromPubkey: publicKey,
                    toPubkey: wsolATA,
                    lamports: Number(lamports), // Convert bigint to number
                })
            )

            // Sync the native account so it becomes WSOL.
            instructions.push(createSyncNativeInstruction(wsolATA))

            // Create a base transaction.
            const { blockhash } = await connection.getLatestBlockhash()
            const tx = new Transaction()
            tx.add(...instructions)
            tx.recentBlockhash = blockhash
            tx.feePayer = publicKey

            // Convert the transaction into a versioned transaction.
            const transactionMessageV0 = new anchor.web3.TransactionMessage({
                payerKey: publicKey,
                recentBlockhash: blockhash,
                instructions: tx.instructions,
            }).compileToV0Message()
            const versionedTransaction = new anchor.web3.VersionedTransaction(transactionMessageV0)

            // Note: We do NOT sign the transaction here.
            return { versionedTransaction, blockhash }
        },
        [publicKey]
    )

    /**
     * Executes the wrap SOL transaction:
     * - Uses getWrapData to construct the versioned transaction.
     * - Requests the wallet to sign it.
     * - Sends the signed transaction to the network.
     */
    const wrapSol = useCallback(
        async (lamports: bigint): Promise<string> => {
            if (!publicKey) throw new Error('Wallet not connected')
            if (!signTransaction) throw new Error('Wallet does not support signing')
            if (!lamports || lamports <= BigInt(0)) {
                throw new Error('Must wrap a positive amount of SOL (lamports)')
            }

            try {
                logger.info('Preparing wrap SOL transaction', {
                    lamports,
                    user: publicKey.toString(),
                })

                // We don't need to store the connection since we're using the retry utility
                const { versionedTransaction } = await getWrapData(lamports, publicKey)
                if (!signTransaction) throw new Error('Wallet does not support transaction signing')
                const signedTx = await signTransaction(versionedTransaction)

                // Send transaction with retry mechanism
                const signature = await sendTransactionWithRetry(signedTx, {
                    skipPreflight: true,
                    preflightCommitment: 'processed',
                    maxRetries: 5,
                })

                logger.info('Wrap SOL transaction sent', {
                    signature,
                    lamports,
                })

                return signature
            } catch (error) {
                logger.error('Error wrapping SOL:', { error })
                throw error
            }
        },
        [publicKey, signTransaction, getWrapData]
    )

    /**
     * Waits for the wrap SOL transaction to confirm.
     * @param txSignature The signature of the wrap SOL transaction.
     */
    const waitForWrap = useCallback(async (txSignature: string) => {
        try {
            await confirmTransactionWithRetry(txSignature, {
                preflightCommitment: 'processed',
                maxRetries: 5,
            })
            logger.info('Wrap transaction confirmed', { txSignature })
        } catch (error) {
            logger.error('Error waiting for wrapSol confirmation:', { error })
            throw error
        }
    }, [])

    return { wrapSol, waitForWrap, getWrapData }
}
